import { useTranslation } from "react-i18next";

import { useCan } from "components";
import { APPOINTMENT_NOTE_TYPES, STATUS_IDENTIFIERS } from "models";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { isCarReadyOrQualityCheck } from "utils/appointmentUtils";

const APPOINTMENT_NOTES_CUSTOM_ORDER = [
  APPOINTMENT_NOTE_TYPES.Info,
  APPOINTMENT_NOTE_TYPES.BackOrder,
  APPOINTMENT_NOTE_TYPES.NoDrive,
  APPOINTMENT_NOTE_TYPES.MakeAnOffer,
  APPOINTMENT_NOTE_TYPES.WO,
  APPOINTMENT_NOTE_TYPES.Main,
  APPOINTMENT_NOTE_TYPES.CallCustomer,
  APPOINTMENT_NOTE_TYPES.Attachment,
  APPOINTMENT_NOTE_TYPES.Bill,
  APPOINTMENT_NOTE_TYPES.TempDriver,
  APPOINTMENT_NOTE_TYPES.RecurringCar,
  APPOINTMENT_NOTE_TYPES.ParkingLocation
];

export const useNoteTypeOptions = (appointmentStatusIdentifier: STATUS_IDENTIFIERS) => {
  const { t } = useTranslation();

  const selectedLocation = useAppointmentLocation();
  const canMain = useCan("create", "appointment-notes");
  const canWO = useCan("create", "appointment-notes-wo");
  const canInfo = useCan("create", "appointments-info");
  const canBill = useCan("create", "appointment-notes-bills");
  const canNoDrive = useCan("create", "appointment-notes-no-drive");
  const canBackOrder = useCan("create", "appointments-backorder");
  const canAttachment = useCan("create", "appointment-notes-attachments");
  const canTempDriver = useCan("create", "appointment-notes-temp-driver");
  const canMakeAnOffer = useCan("create", "appointments-make-an-offer");
  const canRecurringCar = useCan("create", "appointment-notes-recurring-car");
  const canCallCustomer = useCan("create", "appointment-notes-customer-call");
  const canParkingLocation = useCan("create", "appointment-notes-parking-location");

  const isDiagnoseStatus = appointmentStatusIdentifier === STATUS_IDENTIFIERS.DiagnoseStatus;
  const isPricingOkStatus = appointmentStatusIdentifier === STATUS_IDENTIFIERS.PricingOKStatus;
  const isCarCheckOrBackOrderStatusActive = [STATUS_IDENTIFIERS.CarCheckStartedStatus, STATUS_IDENTIFIERS.BackOrder].includes(appointmentStatusIdentifier);
  const isCarReadyQualityCheckOrCustomerOKStatus =
    appointmentStatusIdentifier === STATUS_IDENTIFIERS.CustomerOKStatus || isCarReadyOrQualityCheck(appointmentStatusIdentifier);

  const appointmentNoteTypeConfigMap = new Map<APPOINTMENT_NOTE_TYPES, { text: string; disabled: boolean }>([
    [APPOINTMENT_NOTE_TYPES.Info, { text: t("v8_set_info_status", "Set Info status"), disabled: !(canInfo && !isCarCheckOrBackOrderStatusActive && !isDiagnoseStatus) }],
    [APPOINTMENT_NOTE_TYPES.BackOrder, { text: t("v8_set_back_order", "Set Back Order"), disabled: !(canBackOrder && isCarReadyQualityCheckOrCustomerOKStatus) }],
    [APPOINTMENT_NOTE_TYPES.NoDrive, { text: t("v8_set_no_drive_status", "Set No Drive Status"), disabled: !canNoDrive }],
    [APPOINTMENT_NOTE_TYPES.MakeAnOffer, { text: t("v8_set_make_offer_status", "Set Make an offer status"), disabled: !(canMakeAnOffer && isPricingOkStatus) }],
    [APPOINTMENT_NOTE_TYPES.WO, { text: t("v8_add_note", "Add Note"), disabled: !canWO }],
    [APPOINTMENT_NOTE_TYPES.Main, { text: t("v8_add_main_note", "Add Main Note"), disabled: !canMain }],
    [APPOINTMENT_NOTE_TYPES.CallCustomer, { text: t("v8_call_customer_note", "Call Customer Note"), disabled: !canCallCustomer }],
    [APPOINTMENT_NOTE_TYPES.Attachment, { text: t("v8_attachment", "Add Attachment(s)"), disabled: !canAttachment }],
    [APPOINTMENT_NOTE_TYPES.TempDriver, { text: t("v8_temporary_driver_note", "Add Temporary Driver"), disabled: !canTempDriver }],
    [APPOINTMENT_NOTE_TYPES.RecurringCar, { text: t("v8_add_recurring_car", "Add Recurring Car"), disabled: !canRecurringCar }],
    [APPOINTMENT_NOTE_TYPES.ParkingLocation, { text: t("v8_add_parking_location", "Add parking location"), disabled: !canParkingLocation }],
    [
      APPOINTMENT_NOTE_TYPES.Bill,
      {
        text: t("v8_add_customer_bill", "Add Customer Bill"),
        disabled: !(canBill && isCarReadyQualityCheckOrCustomerOKStatus && selectedLocation?.repair_overview_enabled)
      }
    ]
  ]);

  const enabledNoteTypes = APPOINTMENT_NOTES_CUSTOM_ORDER.filter(type => selectedLocation?.enabled_appointment_note_type_ids?.includes(type));
  return { noteTypeOptions: enabledNoteTypes.map(nt => ({ key: nt, value: nt, ...appointmentNoteTypeConfigMap.get(nt) })) };
};
